import { useEffect, useMemo, useState } from 'react';
import { Header } from '@/components';
import { useAppStore } from '@/stores/appStore';
import { loadHotListDb, removeHotListEntityDb, type HotListEntity } from '@/services/hotLists';
import { RefreshCw, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function MyListsHot() {
  const { userEmail } = useAppStore();
  const navigate = useNavigate();
  const [items, setItems] = useState<HotListEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const load = async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const rows = await loadHotListDb(userEmail);
      setItems(rows);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().catch(() => undefined);
  }, [userEmail]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (sourceFilter && item.source !== sourceFilter) return false;
      if (!q) return true;
      const hay = `${item.title} ${item.subtitle || ''} ${item.source} ${item.platform || ''} ${item.url || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, sourceFilter]);

  const uniqueSources = useMemo(() => Array.from(new Set(items.map((i) => i.source))).sort(), [items]);

  const removeItem = async (id: string) => {
    if (!userEmail) return;
    const next = await removeHotListEntityDb(userEmail, id);
    setItems(next);
  };

  const routeForItem = (item: HotListEntity) => {
    if (item.source === 'podcast_search' || item.source === 'podcast_episode') return '/podcasts';
    if (item.source === 'listener_search' || item.source === 'social_scrape') return '/social-listener';
    return '/my-lists/contacts';
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <div className="px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">My Lists - Hot Lists</h2>
            <p className="text-sm text-gray-400 mt-1">Priority entities saved from scraping and listening workflows.</p>
          </div>
          <button
            onClick={() => load()}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="px-6 py-4 border-b border-gray-800 bg-card-dark">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[260px] flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search hot list..."
              className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm"
            />
          </div>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm"
          >
            <option value="">All Sources</option>
            {uniqueSources.map((src) => (
              <option key={src} value={src}>{src}</option>
            ))}
          </select>
          <span className="text-sm text-gray-400">
            Total: <span className="text-white font-semibold">{filtered.length}</span>
          </span>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="bg-card-dark border border-gray-700 rounded-xl overflow-hidden">
          <div className="grid grid-cols-[2fr_1.2fr_1fr_1fr_220px] border-b border-gray-700 text-xs uppercase text-gray-400 font-semibold">
            <div className="px-3 py-3">Entity</div>
            <div className="px-3 py-3">Source</div>
            <div className="px-3 py-3">Platform</div>
            <div className="px-3 py-3">Created</div>
            <div className="px-3 py-3 text-right">Actions</div>
          </div>
          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-gray-500 text-sm">No hot list entities found.</div>
          ) : (
            filtered.map((item) => (
              <div key={item.id} className="grid grid-cols-[2fr_1.2fr_1fr_1fr_220px] border-b border-gray-800 text-sm">
                <div className="px-3 py-3 min-w-0">
                  <div className="text-white truncate">{item.title}</div>
                  {item.subtitle ? <div className="text-xs text-gray-400 truncate">{item.subtitle}</div> : null}
                </div>
                <div className="px-3 py-3 text-gray-300">{item.source}</div>
                <div className="px-3 py-3 text-gray-300">{item.platform || '—'}</div>
                <div className="px-3 py-3 text-gray-400">{new Date(item.createdAt).toLocaleString()}</div>
                <div className="px-3 py-3 flex justify-end gap-2">
                  <button
                    onClick={() => navigate(routeForItem(item))}
                    className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-xs"
                  >
                    Research
                  </button>
                  {item.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-xs"
                    >
                      Open
                    </a>
                  ) : null}
                  <button
                    onClick={() => removeItem(item.id)}
                    className="px-2 py-1 rounded bg-red-800 hover:bg-red-700 text-xs"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
